import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, Typography } from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import QRCode from "qrcode";
import { getCommunityStatus, joinCommunity } from "../api/community";
import { SERVER_URL } from "../api";
import { ME_URL } from "../utils";
import { event } from "../utils/gtag";

const GREEN = "#66bc5f";

function resolveLogo(url) {
  if (!url || typeof url !== "string" || !url.trim()) return null;
  return url.startsWith("http") ? url : `${SERVER_URL}/${url}`;
}

function languageCode(i18n) {
  return String(i18n.language || "es").split("-")[0].toLowerCase();
}

/** Fila de sellos: círculos llenos hasta `count` de `total`. */
function StampsRow({ count, total }) {
  const safeTotal = Math.max(1, Math.min(Number(total) || 5, 20));
  const filled = Math.max(0, Math.min(Number(count) || 0, safeTotal));
  return (
    <Box
      aria-hidden="true"
      style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 6, margin: "10px 0" }}
    >
      {Array.from({ length: safeTotal }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 26,
            height: 26,
            borderRadius: "50%",
            border: `2px solid ${GREEN}`,
            backgroundColor: i < filled ? GREEN : "transparent",
            color: "#fff",
            fontSize: 14,
            lineHeight: "22px",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          {i < filled ? "✓" : ""}
        </span>
      ))}
    </Box>
  );
}

function CouponCard({ coupon, t, i18n }) {
  const [qr, setQr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(coupon.code, { width: 160, margin: 1 })
      .then((url) => !cancelled && setQr(url))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [coupon.code]);

  const date = new Date(coupon.expiresAt).toLocaleDateString(i18n.language || "es", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return (
    <Box
      style={{
        border: `2px dashed ${GREEN}`,
        borderRadius: 12,
        padding: "14px 12px",
        margin: "12px auto 0",
        maxWidth: 320,
        backgroundColor: "#fff",
      }}
    >
      <Typography style={{ fontWeight: 700, color: "#2f6b2a", fontSize: 16 }}>
        {t("community.rewardTitle", { reward: coupon.recompensa })}
      </Typography>
      <Typography variant="body2" style={{ color: "#666", marginTop: 8 }}>
        {t("community.couponCode")}
      </Typography>
      <Typography
        style={{ fontFamily: "'Courier New', monospace", fontWeight: 700, fontSize: 30, letterSpacing: 5 }}
      >
        {coupon.code}
      </Typography>
      {qr && <img src={qr} alt={coupon.code} style={{ width: 140, height: 140, marginTop: 4 }} />}
      <Typography variant="body2" style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
        {t("community.couponHint", { date })}
      </Typography>
    </Box>
  );
}

/**
 * Bloque "Comunidad del negocio" de la pantalla de éxito.
 * - Programa inactivo (o plan Free): no pinta nada.
 * - Tiper sin identificar: invita a dejar el email (si el formulario está visible).
 * - Identificado y no miembro: texto + casilla de consentimiento (desmarcada) + Unirme.
 * - Miembro: progreso de sellos y cupones activos.
 */
const CommunityBlock = ({ businessId, tipId, tiperId, emailFormVisible, refreshKey }) => {
  const { t, i18n } = useTranslation();
  const lang = languageCode(i18n);
  const [status, setStatus] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(false);
  const [justJoined, setJustJoined] = useState(null);

  const load = useCallback(async () => {
    if (!businessId) return;
    try {
      const res = await getCommunityStatus(businessId, { tiperId, tipId, lang });
      setStatus(res?.data || null);
    } catch (error) {
      setStatus(null);
    }
  }, [businessId, tiperId, tipId, lang, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
  }, [load]);

  if (!status?.active) return null;

  const businessName = status.business?.nombre || "";
  const reward = status.program?.recompensa || "";
  const total = status.program?.sellosNecesarios || 5;
  const member = status.member;
  const identified = status.tiper?.identified === true;

  if (!member && !identified && !emailFormVisible) return null;

  const handleJoin = async () => {
    if (!accepted || joining) return;
    setJoining(true);
    setJoinError(false);
    try {
      const res = await joinCommunity(businessId, {
        tiperId,
        tipId,
        accepted: true,
        consentVersion: status.consentVersion,
        language: lang,
      });
      setJustJoined({ stamped: res?.data?.stamp?.stamped === true });
      setStatus((prev) => ({ ...prev, member: res?.data?.member || prev.member }));
      event({ action: "unirse_comunidad", category: "Éxito", label: "SuccessScreen" });
    } catch (error) {
      setJoinError(true);
    }
    setJoining(false);
  };

  const logo = resolveLogo(status.business?.logoURL);

  return (
    <div className="d-flex flex-column text-center ss-community">
      <Box
        style={{
          backgroundColor: "#f3faf2",
          border: "1px solid #d6efd3",
          borderRadius: 16,
          padding: "18px 16px",
          margin: "0 auto",
          maxWidth: 400,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {logo && (
          <img
            src={logo}
            alt={businessName}
            style={{ maxHeight: 40, maxWidth: 120, objectFit: "contain", marginBottom: 6 }}
          />
        )}
        <Typography style={{ fontWeight: 700, fontSize: 17, color: "#2f6b2a" }}>
          {member
            ? t("community.memberTitle", { business: businessName })
            : t("community.joinTitle", { business: businessName })}
        </Typography>

        {member ? (
          <>
            {justJoined && (
              <Typography variant="body2" style={{ color: "#2e7d32", marginTop: 6, fontWeight: 500 }}>
                {justJoined.stamped ? t("community.welcome") : t("community.welcomeNoStamp")}
              </Typography>
            )}
            <StampsRow count={member.sellos} total={member.sellosNecesarios || total} />
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {t("community.progress", { count: member.sellos, total: member.sellosNecesarios || total })}
            </Typography>
            <Typography variant="body2" style={{ color: "#666", marginTop: 2 }}>
              {t("community.stampsRule", { total: member.sellosNecesarios || total, reward })}
            </Typography>
            {(member.coupons || []).map((coupon) => (
              <CouponCard key={coupon.id} coupon={coupon} t={t} i18n={i18n} />
            ))}
            <Button
              component="a"
              href={ME_URL}
              target="_blank"
              rel="noopener noreferrer"
              variant="text"
              style={{ color: GREEN, textTransform: "none", fontWeight: 600, marginTop: 8 }}
              onClick={() => event({ action: "abrir_tipify_me_comunidad", category: "Éxito", label: "SuccessScreen" })}
            >
              {t("community.viewInMe")}
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" style={{ marginTop: 8, lineHeight: 1.45 }}>
              <Trans
                i18nKey="community.text"
                values={{ business: businessName, reward }}
                components={{ b: <strong /> }}
              />
            </Typography>
            <StampsRow count={0} total={total} />
            <Typography variant="body2" style={{ color: "#666" }}>
              {t("community.stampsRule", { total, reward })}
            </Typography>

            {!identified ? (
              <Typography variant="body2" style={{ marginTop: 10, color: "#2f6b2a", fontWeight: 500 }}>
                {t("community.needEmail")}
              </Typography>
            ) : (
              <>
                <Box
                  style={{
                    marginTop: 12,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <Checkbox
                    checked={accepted}
                    onChange={(e) => setAccepted(e.target.checked)}
                    size="small"
                    inputProps={{ "aria-label": t("community.consent", { business: businessName }) }}
                    style={{ padding: 4, marginTop: -6, paddingLeft: 0 }}
                  />
                  <Typography variant="body2" style={{ fontSize: 12, textAlign: "left", lineHeight: 1.4 }}>
                    {t("community.consent", { business: businessName })}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  style={{ fontSize: 11, color: "#777", textAlign: "left", marginTop: 6, lineHeight: 1.35 }}
                >
                  {t("community.note", { business: businessName })}
                </Typography>
                <Button
                  variant="contained"
                  disabled={!accepted || joining}
                  onClick={handleJoin}
                  style={{
                    marginTop: 12,
                    backgroundColor: !accepted || joining ? "#c8e6c4" : GREEN,
                    color: "#fff",
                    borderRadius: 30,
                    textTransform: "none",
                    fontWeight: 600,
                    padding: "8px 28px",
                  }}
                >
                  {joining ? <CircularProgress size={18} style={{ color: "#fff" }} /> : t("community.join")}
                </Button>
                {joinError && (
                  <Typography variant="body2" style={{ color: "#c62828", marginTop: 8 }}>
                    {t("community.error")}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </div>
  );
};

export default CommunityBlock;
